import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { useGetUserSubscriptionQuery } from '@/generated/graphql';
import DialogWindow from '../DialogWindow';
import StickyHeader from './components/StickyHeader';
import ProjectList from './components/ProjectList';
import { useProjectSelections } from './hooks/useProjectSelections';
import { useProjectSettings } from './hooks/useProjectSettings';
import {
  BatchProjectUploadDialogProps,
  SubscriptionInfo,
} from './models/types';
import {
  buildSelectedProjectSummary,
  buildSelectedUploadItems,
  calculateStorageUsage,
  sortProjects,
} from '../../../utils/batchProjectUploadUtils';
import { PaginationOptions } from '../../../models/pagination';

const BatchProjectUploadDialog: React.FC<BatchProjectUploadDialogProps> = ({
  open,
  onClose,
  detectedProjects,
  existingProjects,
  onUpload,
  isUploading = false,
  uploadProgress = {},
}) => {
  const {
    selectedProjects,
    expandedProjects,
    toggleProject,
    toggleAll,
    toggleExpand,
    setExpandedProjects,
    selectProjects,
    deselectProjects,
  } = useProjectSelections(detectedProjects);

  const { projectSettings, updateProjectSetting } = useProjectSettings(
    detectedProjects,
    existingProjects
  );

  const [pagination, setPagination] = useState<PaginationOptions>({
    sortField: 'name',
    ascending: true,
  });

  const handleSortFieldChange = (sortField: string) => {
    setPagination((prev) => ({ ...prev, sortField }));
  };

  const handleSortDirectionToggle = () => {
    setPagination((prev) => ({ ...prev, ascending: !prev.ascending }));
  };

  // Apply sorting to detected projects
  const sortedProjects = useMemo(
    () =>
      sortProjects(
        detectedProjects,
        pagination.sortField,
        pagination.ascending
      ),
    [detectedProjects, pagination.sortField, pagination.ascending]
  );

  const { data: subscriptionData } = useGetUserSubscriptionQuery({
    fetchPolicy: 'network-only',
  });

  const subscription = subscriptionData?.user
    ?.subscription as SubscriptionInfo | null;

  const summary = buildSelectedProjectSummary(
    sortedProjects,
    selectedProjects,
    projectSettings
  );

  const storageUsage = calculateStorageUsage(
    subscription,
    summary.totalSelectedSize
  );

  const areSelectedExpanded =
    summary.selectedCount > 0 &&
    Array.from(selectedProjects).every((name) => expandedProjects.has(name));

  const handleDescriptionChange = (folderPath: string, description: string) => {
    updateProjectSetting(folderPath, 'description', description);
  };

  const handleRendersFolderChange = (
    folderPath: string,
    folderName: string
  ) => {
    updateProjectSetting(folderPath, 'rendersFolderName', folderName);
  };

  const handleToggleSelectedExpansion = () => {
    const selectedProjectNames = Array.from(selectedProjects);
    const allSelectedExpanded = selectedProjectNames.every((name) =>
      expandedProjects.has(name)
    );

    const nextExpanded = new Set(expandedProjects);
    if (allSelectedExpanded) {
      selectedProjectNames.forEach((name) => nextExpanded.delete(name));
    } else {
      selectedProjectNames.forEach((name) => nextExpanded.add(name));
    }
    setExpandedProjects(nextExpanded);
  };

  const handleUpload = () => {
    const selectedProjectData = buildSelectedUploadItems(
      sortedProjects,
      selectedProjects,
      projectSettings
    );
    onUpload(selectedProjectData);
  };

  // Build selected projects data for the storage bar
  const selectedProjectsData = sortedProjects
    .filter((project) => selectedProjects.has(project.folderPath))
    .map((project) => {
      const settings = projectSettings[project.folderPath] || {};
      return {
        folderPath: project.folderPath,
        projectName: settings.projectName || project.projectName,
        totalSize: project.totalSize,
      };
    });

  // Scroll to project card when clicked in storage bar
  const handleProjectClick = (folderPath: string) => {
    const element = document.getElementById(`project-card-${folderPath}`);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      // Highlight the card briefly
      element.style.transition = 'box-shadow 0.3s ease';
      element.style.boxShadow = 'var(--sv-focus-ring)';
      setTimeout(() => {
        element.style.boxShadow = '';
      }, 1000);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <DialogWindow
      open={open}
      onClose={handleClose}
      title={`Detected Projects (${detectedProjects.length})`}
      maxWidth="md"
      fullWidth
      disableClose={false}
    >
      <StickyHeader
        subscription={subscription}
        storageUsage={storageUsage}
        summary={summary}
        selectedProjectsCount={selectedProjects.size}
        totalDetectedProjects={detectedProjects.length}
        selectedProjects={selectedProjectsData}
        detectedProjects={detectedProjects}
        projectSettings={projectSettings}
        selectedProjectsSet={selectedProjects}
        toggleAll={toggleAll}
        selectProjects={selectProjects}
        deselectProjects={deselectProjects}
        areSelectedExpanded={areSelectedExpanded}
        onToggleExpandSelected={handleToggleSelectedExpansion}
        isUploading={isUploading}
        uploadProgress={uploadProgress}
        onProjectClick={handleProjectClick}
        pagination={pagination}
        onSortFieldChange={handleSortFieldChange}
        onSortDirectionToggle={handleSortDirectionToggle}
      />

      <DialogContent>
        {detectedProjects.length === 0 ? (
          <Alert severity="info">
            No DAW project files detected in the selected directory.
            <br />
            <strong>Looking for:</strong> DAW project files in the selected
            folder or its subfolders (.als, .logicx, .ptf, .cpr, .flp, etc.)
          </Alert>
        ) : (
          <Box>
            <ProjectList
              projects={sortedProjects}
              selectedProjects={selectedProjects}
              expandedProjects={expandedProjects}
              projectSettings={projectSettings}
              isUploading={isUploading}
              onToggleSelect={toggleProject}
              onToggleExpand={toggleExpand}
              onDescriptionChange={handleDescriptionChange}
              onRendersFolderChange={handleRendersFolderChange}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={
            summary.selectedCount === 0 ||
            isUploading ||
            storageUsage.wouldExceedLimit ||
            summary.duplicateCount > 0
          }
          startIcon={<UploadIcon />}
          color={
            storageUsage.wouldExceedLimit || summary.duplicateCount > 0
              ? 'error'
              : 'primary'
          }
        >
          {isUploading
            ? `Uploading ${summary.selectedCount} Projects...`
            : storageUsage.wouldExceedLimit
              ? `Upload Exceeds Storage Limit`
              : summary.duplicateCount > 0
                ? `Cannot Upload: ${summary.duplicateCount} Duplicate${
                    summary.duplicateCount > 1 ? 's' : ''
                  } Selected`
                : `Upload ${summary.selectedCount} Selected Projects`}
        </Button>
      </DialogActions>
    </DialogWindow>
  );
};

export default BatchProjectUploadDialog;
